import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, Truck, ArrowRight, CheckCircle, AlertCircle, Beaker, Sparkles, Gift, Users, Award, Shield, Package, Wine, Leaf, Image, ChevronLeft, ChevronRight, Play, X } from 'lucide-react';
import { collectionsAPI } from '../services/api';
import { useAPI } from '../hooks/useAPI';
import { usePageTitle } from '../hooks/usePageTitle';
import { mockNewsArticles } from '../data/mock';
import Header from './Header';
import Footer from './Footer';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import RobustImage from './ui/RobustImage';
import { useToast } from '../hooks/use-toast';
import SEO from './SEO';
import { organizationSchema, websiteSchema } from '../utils/structuredData';
import '../styles/modern-design.css';

const Homepage = () => {
  usePageTitle('Home');
  
  // Newsletter state
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const { toast } = useToast();
  
  // Video modal state
  const [showVideoModal, setShowVideoModal] = useState(false);
  const videoId = 'f7C_r3mG2NQ';

  // Carousel state for Recipe Section
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const recipeImages = [
    {
      url: '/images/amaro-1.webp',
      alt: 'Amaro personalizzato 1'
    },
    {
      url: '/images/amaro-2.webp',
      alt: 'Amaro personalizzato 2'
    },
    {
      url: '/images/amaro-3.webp',
      alt: 'Amaro personalizzato 3'
    },
    {
      url: '/images/amaro-4.webp',
      alt: 'Amaro personalizzato 4'
    }
  ];

  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % recipeImages.length
      );
    }, 4000); // Cambia immagine ogni 4 secondi

    return () => clearInterval(interval);
  }, [recipeImages.length]);

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      (prevIndex + 1) % recipeImages.length
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? recipeImages.length - 1 : prevIndex - 1
    );
  };

  // Load featured collections
  const { data: featuredCollections, loading: collectionsLoading, error: collectionsError } = useAPI(() => 
    collectionsAPI.getFeaturedCollections(6)
  );
  
  // B2B Reviews - Hardcoded
  const b2bReviews = [
    {
      id: 1,
      company: "Ristorante La Taverna del Porto",
      text: "Abbiamo realizzato L'Amaro del Porto per offrire ai clienti qualcosa di unico. Il risultato è stato straordinario: un prodotto che racconta la nostra identità e che tutti chiedono a fine pasto. Servizio impeccabile e tempi rapidi.",
      rating: 5
    },
    {
      id: 2,
      company: "Hotel Magna Grecia Resort",
      text: "Cercavamo un'idea elegante da proporre come omaggio agli ospiti. Con iltuoamaro.it abbiamo creato un amaro personalizzato con il nostro logo. È diventato un vero simbolo del nostro brand.",
      rating: 5
    },
    {
      id: 3,
      company: "Pizzeria Da Peppone",
      text: "Abbiamo scelto una ricetta con liquirizia e bergamotto, perfetta dopo i nostri piatti. I clienti la riconoscono subito e la chiedono per nome. Un'idea vincente che ci distingue dalla concorrenza.",
      rating: 5
    },
    {
      id: 4,
      company: "Enoteca Vigna & Mare",
      text: "Abbiamo creato tre amari personalizzati per la nostra linea degustazione. Ogni bottiglia ha un carattere diverso, ma lo stile è sempre coerente con il nostro marchio. Ottimo supporto e qualità eccellente.",
      rating: 5
    },
    {
      id: 5,
      company: "Agriturismo Le Querce Antiche",
      text: "Abbiamo utilizzato le erbe aromatiche del nostro giardino per la ricetta. Il risultato è un amaro autentico, che racconta la nostra filosofia naturale e artigianale. I clienti lo acquistano come ricordo dell'esperienza.",
      rating: 5
    },
    {
      id: 6,
      company: "Birrificio Artigianale Ionico",
      text: "Con iltuoamaro.it abbiamo creato un liquore che si abbina perfettamente alle nostre birre. Un mix di luppolo, agrumi e spezie che piace a tutti. Professionalità e competenza in ogni fase.",
      rating: 5
    },
    {
      id: 7,
      company: "Ristorante Al Vecchio Teatro",
      text: "Servizio personalizzato nei minimi dettagli: dalla ricetta all'etichetta. Hanno capito subito il nostro stile e tradotto la nostra idea in un prodotto di altissima qualità.",
      rating: 5
    },
    {
      id: 8,
      company: "Azienda Agricola Fior di Campo",
      text: "Abbiamo creato il nostro amaro con ingredienti coltivati in azienda. Il risultato è un liquore autentico, con un packaging raffinato che valorizza anche la nostra immagine aziendale.",
      rating: 5
    },
    {
      id: 9,
      company: "Lounge Bar 47",
      text: "Volevamo un amaro esclusivo per i nostri cocktail. Iltuoamaro.it ci ha aiutato a creare una ricetta unica con note agrumate e speziate. È diventato un ingrediente fisso del nostro menù signature.",
      rating: 5
    },
    {
      id: 10,
      company: "Azienda Eventi & Catering Starlight",
      text: "Abbiamo scelto di personalizzare le bottiglie come gadget per i nostri clienti. Ogni evento ha ora un tocco distintivo. Ottima idea, grande qualità e servizio rapido!",
      rating: 5
    }
  ];

  // Newsletter subscription handler
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!newsletterEmail || !newsletterEmail.includes('@')) {
      toast({
        title: "Email non valida",
        description: "Inserisci un indirizzo email valido",
        variant: "destructive"
      });
      return;
    }

    setNewsletterLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: newsletterEmail
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setNewsletterSuccess(true);
        setNewsletterEmail('');
        toast({
          title: "Iscrizione completata!",
          description: result.message,
          variant: "default"
        });
      } else {
        toast({
          title: "Errore nell'iscrizione",
          description: result.message,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast({
        title: "Errore di connessione",
        description: "Si è verificato un errore. Riprova più tardi.",
        variant: "destructive"
      });
    } finally {
      setNewsletterLoading(false);
    }
  };

  // Fetch data from APIs

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Il Tuo Amaro | Liquori Personalizzati B2B - Migliore Distilleria Italiana 2025"
        description="Liquorificio Lacinio - Migliore Distilleria Italiana 2025 e produttore del Miglior Amaro al Mondo. Crea liquori artigianali personalizzati per la tua attività. Ordine minimo 24 bottiglie. Ingredienti calabresi premium, tradizione dal 2019."
        keywords="amaro personalizzato b2b, liquori artigianali calabria, migliore distilleria italiana, liquorificio lacinio, amaro su misura, gin personalizzato, bitter artigianale, liquori made in italy, produzione liquori crotone"
        url="/"
        structuredData={[organizationSchema, websiteSchema]}
      />
      <Header />
      
      {/* Hero Section - Modern Style */}
      <section className="hero-modern relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Stats Banner - B2B - Hidden on mobile */}
          <div className="text-center mb-8 hidden md:block">
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 text-sm font-medium">
              <Users className="w-4 h-4 mr-2 text-amber-300" />
              Partner di fiducia per <strong className="mx-1">200+</strong> professionisti HoReCa
            </div>
          </div>
          
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
              <span className="text-white">Liquori Personalizzati</span><br />
              <span className="text-amber-300 font-black">
                Per il Tuo Business
              </span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90 leading-relaxed max-w-3xl mx-auto">
              Forniture professionali di <strong className="text-amber-300">liquori artigianali personalizzati</strong> per ristoranti, bar, enoteche e distributori.
              <br className="hidden md:block" />
              <span className="text-lg hidden md:inline">Ordine minimo 24 bottiglie • Consegne palletizzate</span>
            </p>
            
            {/* B2B Benefits - Hidden on mobile */}
            <div className="mb-8 hidden md:flex flex-wrap justify-center gap-4 text-sm text-white/80">
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-2 text-green-400" />
                Prezzi all'ingrosso
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-2 text-green-400" />
                Sconti volume
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-2 text-green-400" />
                Spedizione gratuita*
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link 
                to="/builder" 
                className="btn-modern-primary inline-flex items-center text-lg px-10 py-4 transform hover:scale-105"
              >
                Crea il Tuo Liquore
                <ArrowRight className="ml-3 w-6 h-6" />
              </Link>
              <Link 
                to="/register" 
                className="btn-modern-secondary inline-flex items-center text-lg px-10 py-4 transform hover:scale-105"
              >
                Registrati come Partner
                <Users className="ml-3 w-6 h-6" />
              </Link>
              
              {/* Video Play Button - Pulsing Circle Icon */}
              <button
                onClick={() => setShowVideoModal(true)}
                className="relative inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white rounded-full transition-all duration-300 transform hover:scale-110 border-2 border-white/40 group"
                aria-label="Guarda il video di presentazione"
              >
                {/* Pulsing Animation Ring */}
                <span className="absolute inline-flex h-full w-full rounded-full bg-white/30 opacity-75 animate-ping"></span>
                <span className="absolute inline-flex h-full w-full rounded-full bg-white/20 opacity-50 animate-pulse"></span>
                
                {/* Play Icon */}
                <Play className="relative w-8 h-8 md:w-10 md:h-10 fill-white group-hover:scale-110 transition-transform" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-1/4 left-10 floating opacity-20">
          <Beaker className="w-16 h-16 text-white" />
        </div>
        <div className="absolute top-1/3 right-16 floating opacity-20" style={{animationDelay: '1s'}}>
          <Sparkles className="w-12 h-12 text-amber-300" />
        </div>
        <div className="absolute bottom-1/4 right-10 floating opacity-20" style={{animationDelay: '2s'}}>
          <Award className="w-14 h-14 text-white" />
        </div>
      </section>

      {/* Media Section - Parlano di noi */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Parlano di Noi
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              La nostra storia raccontata dai principali media nazionali
            </p>
          </div>

          {/* Media Logos Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center">
            {/* RAI */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:shadow-lg transition-all duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/ts6rqfsu_Logo_of_RAI_%282016%29.svg.png"
                alt="RAI"
                className="w-full h-auto object-contain max-h-16 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* LA7 */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:shadow-lg transition-all duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/7cd09j8v_LA7_-_Logo_2011.svg.png"
                alt="LA7"
                className="w-full h-auto object-contain max-h-16 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* La Repubblica */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:shadow-lg transition-all duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/s2fu7weu_La_Repubblica_logo.png"
                alt="La Repubblica"
                className="w-full h-auto object-contain max-h-16 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* Corriere della Sera */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:shadow-lg transition-all duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/7iqsyttg_corriere.png"
                alt="Corriere della Sera"
                className="w-full h-auto object-contain max-h-16 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* L'Imperatore */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:shadow-lg transition-all duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_6a391856-95e8-45fa-9fee-07ed1ee695e6/artifacts/g1wgxgl9_010324_LogoLImperatore_425x200.jpg"
                alt="L'Imperatore"
                className="w-full h-auto object-contain max-h-16 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* Altri Media */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:shadow-lg transition-all duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/k4kfj7d8_Screenshot%202025-10-16%20alle%2017.10.33.png"
                alt="Media"
                className="w-full h-auto object-contain max-h-16 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>
          </div>

          {/* Trust Badge */}
          <div className="mt-10 text-center">
            <p className="text-sm text-gray-500 italic">
              "La qualità del Liquorificio Lacinio riconosciuta dai più importanti media italiani"
            </p>
          </div>
        </div>
      </section>

       <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              {/* Carousel Container */}
              <div className="relative group">
                {/* Main Image */}
                <div className="relative overflow-hidden rounded-lg shadow-lg aspect-square">
                  <img
                    src={recipeImages[currentImageIndex].url}
                    alt={recipeImages[currentImageIndex].alt}
                    className="w-full h-full object-cover transition-opacity duration-500"
                  />
                  
                  {/* Navigation Buttons */}
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    aria-label="Immagine precedente"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    aria-label="Immagine successiva"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </div>

                {/* Indicators */}
                <div className="flex justify-center gap-2 mt-4">
                  {recipeImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentImageIndex 
                          ? 'bg-purple-600 w-8' 
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                      aria-label={`Vai all'immagine ${index + 1}`}
                    />
                  ))}
                </div>

                {/* Thumbnail Preview */}
                <div className="grid grid-cols-4 gap-2 mt-4">
                  {recipeImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`relative overflow-hidden rounded aspect-square transition-all duration-300 ${
                        index === currentImageIndex 
                          ? 'ring-4 ring-purple-600 opacity-100' 
                          : 'opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={image.url}
                        alt={image.alt}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Crea il Tuo Amaro <br />Personalizzato
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Scegli gli ingredienti che rispecchiano il tuo gusto e lasciati guidare dall'esperienza dei nostri{' '}
                <span className="font-semibold">mastri liquoristi</span>. 
                Dalle note agrumate e leggere a quelle più intense e amaricanti, potrai decidere il carattere del tuo Amaro, costruendone l'identità passo dopo passo.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Una volta completato l'ordine, la tua ricetta verrà perfezionata dal nostro{' '}
                <span className="font-semibold">mastro distillatore</span>: 
                armonizzerà le essenze che hai scelto, rispettando la tua idea e rendendola{' '}
                <span className="font-semibold">unica, equilibrata e pronta a raccontare la tua storia</span> in ogni goccia.
              </p>
              <Link
                to="/builder"
                className="btn-modern-primary inline-flex items-center text-lg px-8 py-4 transform hover:scale-105"
              >
                Crea la Ricetta
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - B2B */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="modern-grid-3">
            <div className="feature-card fade-in">
              <div className="icon-modern">
                <Package className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">📦 Forniture All'Ingrosso</h3>
              <p className="text-gray-600">
                Ordine minimo 24 bottiglie con sconti progressivi sul volume
              </p>
            </div>
            
            <div className="feature-card fade-in" style={{animationDelay: '0.2s'}}>
              <div className="icon-modern amber">
                <Truck className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">🚚 Consegne Rapide</h3>
              <p className="text-gray-600">
                Spedizioni palletizzate in 7-10 giorni lavorativi
              </p>
            </div>
            
            <div className="feature-card fade-in" style={{animationDelay: '0.4s'}}>
              <div className="icon-modern purple">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">🏆 Qualità Certificata</h3>
              <p className="text-gray-600">
                Produzione artigianale con ingredienti 100% naturali
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section - Modern Style */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title gradient-text mb-6">COME FUNZIONA</h2>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Forniture Professionali in 4 Step
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Crea liquori personalizzati per il tuo business in modo semplice e veloce. 
              Ordine minimo 24 bottiglie • Spedizione gratuita oltre 50 bottiglie
            </p>
          </div>

          <div className="modern-grid-4">
            <div className="step-card slide-in-left">
              <div className="absolute -top-4 left-8 w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                <Wine className="w-6 h-6 text-white" />
              </div>
              <div className="pt-6">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Scegli</h4>
                <h5 className="text-lg font-semibold text-purple-600 mb-3">il liquore</h5>
                <p className="text-gray-600">
                  Seleziona tra Gin, Amaro o Bitter
                </p>
              </div>
            </div>

            <div className="step-card slide-in-left" style={{animationDelay: '0.2s'}}>
              <div className="absolute -top-4 left-8 w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                <Leaf className="w-6 h-6 text-white" />
              </div>
              <div className="pt-6">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Personalizza</h4>
                <h5 className="text-lg font-semibold text-purple-600 mb-3">la ricetta</h5>
                <p className="text-gray-600">
                  Scegli gli ingredienti e l'intensità
                </p>
              </div>
            </div>

            <div className="step-card slide-in-right" style={{animationDelay: '0.4s'}}>
              <div className="absolute -top-4 left-8 w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                <Image className="w-6 h-6 text-white" />
              </div>
              <div className="pt-6">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Crea</h4>
                <h5 className="text-lg font-semibold text-purple-600 mb-3">l'etichetta</h5>
                <p className="text-gray-600">
                  Personalizza il design della bottiglia
                </p>
              </div>
            </div>

            <div className="step-card slide-in-right" style={{animationDelay: '0.6s'}}>
              <div className="absolute -top-4 left-8 w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg">
                <Package className="w-6 h-6 text-white" />
              </div>
              <div className="pt-6">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Scegli</h4>
                <h5 className="text-lg font-semibold text-purple-600 mb-3">la bottiglia</h5>
                <p className="text-gray-600">
                  Seleziona il formato finale
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-16">
            <Link
              to="/builder"
              className="btn-modern-primary inline-flex items-center text-xl px-12 py-5 transform hover:scale-105"
            >
              Inizia Qui →
            </Link>
          </div>
        </div>
      </section>

      {/* Recipe Section */}
     

      {/* Occasions Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm text-purple-600 font-semibold mb-2">Scopri i Vantaggi</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Molto Più di un Semplice Amaro ✨
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Tradizione Calabrese',
                description: 'Ogni bottiglia porta con sé tanti anni di esperienza nella produzione artigianale della famiglia Lacinio.',
                image: 'https://customer-assets.emergentagent.com/job_liquor-order-confirm/artifacts/jep35st6_1.jpg',
                icon: '🏛️'
              },
              {
                title: 'Ingredienti Premium',
                description: 'Solo erbe selezionate del Mediterraneo e spezie rare per un sapore autentico e intenso',
                image: 'https://customer-assets.emergentagent.com/job_liquor-order-confirm/artifacts/2tb5bv22_2.jpg',
                icon: '🌿'
              },
              {
                title: 'Personalizzazione Totale',
                description: 'Dalla ricetta all\'etichetta, ogni dettaglio è nelle tue mani per un risultato davvero unico',
                image: 'https://customer-assets.emergentagent.com/job_liquor-order-confirm/artifacts/4hn2ddfr_3.jpg',
                icon: '🎨'
              }
            ].map((occasion, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="relative overflow-hidden rounded-lg shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                  <img
                    src={occasion.image}
                    alt={occasion.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="mt-6">
                  <p className="text-sm text-purple-600 font-semibold mb-2">{occasion.title}</p>
                  <p className="text-gray-600">{occasion.description}</p>
                  <Link
                    to="/builder"
                    className="inline-flex items-center mt-4 px-4 py-2 bg-purple-50 text-purple-600 hover:text-purple-700 hover:bg-purple-100 font-medium transition-all duration-300 rounded-lg border border-purple-200 hover:border-purple-300"
                  >
                    Vai al Creator
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dynamic Collections Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm text-purple-600 font-semibold mb-2">Ricette della Tradizione</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Le Nostre Collezioni
            </h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              Scopri le nostre collezioni esclusive di liquori artigianali con ricette preimpostate dai nostri maestri distillatori. 
              Ogni collezione racconta una storia unica attraverso sapori autentici e una tradizione senza tempo.
            </p>
          </div>

          {/* Collections Loading/Error States */}
          {collectionsLoading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-gray-600">Caricamento collezioni...</span>
            </div>
          )}

          {collectionsError && (
            <div className="text-center py-12">
              <div className="text-red-600 mb-4">
                <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                <p>Errore nel caricamento delle collezioni</p>
              </div>
              <Link
                to="/campioni"
                className="btn-modern-primary inline-flex items-center text-lg px-8 py-4 transform hover:scale-105"
              >
                Visualizza Tutti i Campioni
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          )}

          {/* Dynamic Collections Grid */}
          {!collectionsLoading && !collectionsError && featuredCollections && featuredCollections.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredCollections.map((collection) => (
                <Link
                  key={collection.id}
                  to={`/collezioni/${collection.slug}`}
                  className="group bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  {/* Collection Image */}
                  <div className="relative h-48 bg-gradient-to-br from-purple-600 to-purple-800">
                    {collection.products && collection.products.length > 0 ? (
                      <RobustImage
                        src={collection.products[0].mainImage}
                        category="products"
                        itemId={collection.products[0].baseProductType || collection.products[0].category?.toLowerCase()}
                        alt={collection.title}
                        className="w-full h-full object-cover"
                      />
                    ) : collection.bannerImage ? (
                      <RobustImage
                        src={collection.bannerImage}
                        category="collections"
                        itemId={collection.slug}
                        alt={collection.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <Package className="w-12 h-12 text-white opacity-70" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-10 transition-all" />
                    
                    {/* Collection Badge */}
                    {collection.isFeatured && (
                      <div className="absolute top-3 right-3">
                        <Star className="w-5 h-5 text-yellow-400 fill-current" />
                      </div>
                    )}
                    
                    {/* Collection Title Overlay */}
                    <div className="absolute bottom-4 left-4 text-white">
                      <h3 className="text-xl font-bold mb-1">{collection.title}</h3>
                      {collection.subtitle && (
                        <p className="text-sm opacity-90">{collection.subtitle}</p>
                      )}
                    </div>
                  </div>

                  {/* Collection Info */}
                  <div className="p-6">
                    <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">
                      {collection.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        {collection.products?.length || 0} {collection.products?.length === 1 ? 'prodotto' : 'prodotti'}
                      </div>
                      <div className="text-purple-600 font-medium text-sm group-hover:text-purple-700 flex items-center">
                        Esplora <ArrowRight className="w-4 h-4 ml-1" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* No Collections Fallback */}
          {!collectionsLoading && !collectionsError && (!featuredCollections || featuredCollections.length === 0) && (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Collezioni in Arrivo</h3>
              <p className="text-gray-600 mb-6">
                Le nostre collezioni esclusive saranno disponibili a breve. 
                Nel frattempo puoi creare il tuo liquore personalizzato.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/builder"
                  className="btn-modern-primary inline-flex items-center text-lg px-8 py-4 transform hover:scale-105"
                >
                  <Beaker className="w-5 h-5 mr-3" />
                  Crea il Tuo Liquore
                </Link>
                <Link
                  to="/campioni"
                  className="inline-flex items-center px-8 py-4 border-2 border-purple-600 text-purple-600 rounded-xl font-semibold hover:bg-purple-50 hover:border-purple-700 transition-all duration-300 transform hover:scale-105"
                >
                  Visualizza Campioni
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
            </div>
          )}

          {/* View All Collections Link */}
          {featuredCollections && featuredCollections.length > 0 && (
            <div className="text-center mt-12">
              <Link
                to="/campioni"
                className="btn-modern-primary inline-flex items-center text-lg px-10 py-5 transform hover:scale-105"
              >
                Visualizza Tutti i Campioni
                <ArrowRight className="w-6 h-6 ml-3" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Made in Italy Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm text-green-600 font-semibold mb-2">Il Processo</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              100% Made In Italy 🇮🇹
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-4xl mx-auto">
              Il tuo Liquore Personalizzato verrà prodotto in un vero{' '}
              <span className="font-semibold">liquorificio artigianale</span>{' '}
              italiano con materie prime certificate e di alta qualità. Ogni bottiglia è realizzata con cura ed interamente a mano in uno{' '}
              <span className="font-semibold">stabilimento certificato</span>.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <CheckCircle className="w-8 h-8" />,
                title: 'Materie Prime Certificate',
                description: 'Utilizziamo solo ingredienti di alta qualità certificati'
              },
              {
                icon: <CheckCircle className="w-8 h-8" />,
                title: 'Produzione Artigianale',
                description: 'Ogni bottiglia è prodotta a mano nel nostro liquorificio'
              },
              {
                icon: <CheckCircle className="w-8 h-8" />,
                title: 'Stabilimento Certificato',
                description: 'Processo produttivo certificato e controllato'
              }
            ].map((feature, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 text-green-600 rounded-full mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards Section - Liquori Premiati */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 text-amber-600 rounded-full mb-6">
              <Award className="w-8 h-8" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              I Nostri Liquori Premiati
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Eccellenze riconosciute a livello nazionale e internazionale
            </p>
          </div>

          {/* Awards Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-8 items-center">
            {/* Logo 1 */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/gkq75uqg_1-1.webp"
                alt="Premio 1"
                className="w-full h-auto object-contain max-h-24 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* Logo 2 */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/cpr55693_2-1.webp"
                alt="Premio 2"
                className="w-full h-auto object-contain max-h-24 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* Logo 3 */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/wjfg2p5q_3-1.webp"
                alt="Premio 3"
                className="w-full h-auto object-contain max-h-24 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* Logo 4 */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/w1yq0v1o_4-1.webp"
                alt="Premio 4"
                className="w-full h-auto object-contain max-h-24 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* Logo 5 - Best Distillery */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/u2jzspfn_Best-Distillery-2025-Italy-1.webp"
                alt="Best Distillery 2025 Italy"
                className="w-full h-auto object-contain max-h-24 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* Logo 6 - Medal 2025 */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/bjh2mev4_medal_2025_1.webp"
                alt="Medal 2025"
                className="w-full h-auto object-contain max-h-24 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* Logo 7 - Medal 2025 #2 */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/2v778kh4_medal_2025_2-1.webp"
                alt="Medal 2025"
                className="w-full h-auto object-contain max-h-24 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* Logo 8 - Ampolla d'Oro */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/dv62pa9i_ampolla-doro.webp"
                alt="Ampolla d'Oro"
                className="w-full h-auto object-contain max-h-24 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* Logo 9 - World Liqueur Awards Silver */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/bmflazcd_WLiqueurA24-Silver.webp"
                alt="World Liqueur Awards Silver"
                className="w-full h-auto object-contain max-h-24 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* Logo 10 - Gold Award */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/yukufh1x_GOLD.webp"
                alt="Gold Award"
                className="w-full h-auto object-contain max-h-24 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* Logo 11 */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/dg87wxof_1.webp"
                alt="Premio 11"
                className="w-full h-auto object-contain max-h-24 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>

            {/* Logo 12 - Silver Award */}
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_b2b-amaro-platform/artifacts/x2lx7knd_SILVER.webp"
                alt="Silver Award"
                className="w-full h-auto object-contain max-h-24 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>
          </div>

          {/* Trust Badge */}
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-500 italic">
              "Qualità e tradizione italiana riconosciute dai più prestigiosi concorsi internazionali"
            </p>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="recensioni" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-8 h-8 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              I Nostri Partner B2B
            </h2>
            <p className="text-sm text-gray-600 font-semibold">
              TESTIMONIANZE DAI NOSTRI CLIENTI PROFESSIONALI
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {b2bReviews.map((review) => (
              <div key={review.id} className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center text-2xl">
                    {['🏪', '🏨', '🍕', '🍷', '🌿', '🍺', '🎭', '🌾', '🍸', '🎉'][
                      (review.id - 1) % 10
                    ]}
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-gray-900">{review.company}</p>
                    <p className="text-sm text-gray-600">Partner B2B</p>
                  </div>
                </div>
                <div className="flex mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">{review.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer First Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 text-blue-600 rounded-full mb-6">
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            Il cliente al primo posto
          </h2>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto">
            La nostra missione è garantire un'esperienza utente unica, per questo motivo, la{' '}
            <span className="font-semibold">spedizione</span> sarà sempre{' '}
            <span className="font-semibold">gratuita</span> senza un minimo d'ordine. Il tuo liquore verrà realizzato ed imbottigliato in un massimo di tre giorni lavorativi e sarà subito pronto per essere spedito.
          </p>
          <div className="mt-8">
            <Link
              to="/faq"
              className="inline-flex items-center px-6 py-3 border-2 border-purple-600 text-purple-600 rounded-xl font-semibold hover:bg-purple-50 hover:border-purple-700 transition-all duration-300 transform hover:scale-105"
            >
              Consulta le nostre FAQ
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 text-white rounded-full mb-6">
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Rimaniamo in contatto
          </h2>
          <p className="text-lg text-purple-100 mb-8 max-w-2xl mx-auto">
            Iscriviti alla nostra newsletter, riceverai notizie sui nuovi prodotti e codici sconto per i tuoi ordini.
          </p>
          {newsletterSuccess ? (
            <div className="max-w-md mx-auto p-6 bg-white/10 rounded-lg backdrop-blur-sm">
              <div className="flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <p className="text-white font-semibold text-lg">Iscrizione completata!</p>
              <p className="text-purple-100 text-sm mt-2">
                Grazie per esserti iscritto alla nostra newsletter.
              </p>
            </div>
          ) : (
            <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto">
              <div className="flex">
                <input
                  type="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="La tua email"
                  className="flex-1 px-4 py-3 rounded-l-lg border-0 focus:ring-2 focus:ring-purple-300 focus:outline-none"
                  disabled={newsletterLoading}
                />
                <button
                  type="submit"
                  disabled={newsletterLoading}
                  className="px-6 py-3 bg-purple-800 text-white rounded-r-lg hover:bg-purple-900 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {newsletterLoading ? 'Invio...' : 'Iscriviti'}
                </button>
              </div>
            </form>
          )}
          <p className="text-xs text-purple-200 mt-4 max-w-2xl mx-auto">
            Liquore Personalizzato tiene particolarmente alla tua privacy e si impegna a garantire la protezione dei tuoi dati personali.
            {' '}
            <Link to="/privacy-policy" className="underline hover:text-white">
              Scopri la nostra informativa sulla privacy
            </Link>
          </p>
        </div>
      </section>

      {/* Video Modal */}
      {showVideoModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setShowVideoModal(false)}
        >
          <div className="relative w-full max-w-5xl mx-4">
            {/* Close Button */}
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-amber-300 transition-colors"
              aria-label="Chiudi video"
            >
              <X className="w-8 h-8" />
            </button>
            
            {/* Video Container */}
            <div 
              className="relative bg-black rounded-lg overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="aspect-video">
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${videoId}?autoplay=1&muted=1&playsinline=1`}
                  title="Video Presentazione IlTuoAmaro"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Homepage;